import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { HeatmapControls } from "./heatmap-controls";
import { useToast } from "@/hooks/use-toast";
import type { Document, DocumentVersion } from "@shared/schema";

interface SliderState {
  isDragging: boolean;
  currentView: 'heatmap' | 'A' | 'B';
  sliderValue: number; // -1 to 1, 0 = heatmap
}

interface HeatmapViewProps {
  document: Document;
  versionA: DocumentVersion;
  versionB: DocumentVersion;
  documentColor: string;
}

interface HeatmapData {
  id: string;
  pageNumber: number;
  heatmapUrl: string;
  diffPixelCount: number;
  totalPixels: number;
  changePercentage: number;
}

// Custom Slider Component
const CustomSlider = ({ value, onChange, onRelease, color, versionA, versionB }: { 
  value: number; 
  onChange: (value: number) => void; 
  onRelease: () => void;
  color: string;
  versionA: number;
  versionB: number;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const updateSliderValue = (clientX: number) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    onChange(percentage);
  };

  const handleStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updateSliderValue(e.clientX);
    
    const handleMove = (e: MouseEvent) => {
      updateSliderValue(e.clientX);
    };
    
    const handleEnd = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      onRelease();
    };
    
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    // Only handle clicks directly on the track, not on the thumb
    if (e.target === e.currentTarget) {
      updateSliderValue(e.clientX);
      onRelease();
    }
  };

  return (
    <div className="flex items-center gap-2 px-2">
      <div className="text-xs text-muted-foreground font-medium">v{versionA}</div>
      <div className="flex-1 relative py-4">
        <div
          ref={sliderRef}
          className="relative w-full h-2 bg-muted rounded-lg cursor-pointer"
          onMouseDown={handleTrackClick}
        >
          {/* Slider Track */}
          <div className="absolute inset-0 bg-muted rounded-lg" />
          
          {/* Slider Thumb */}
          <div
            className="absolute top-1/2 w-6 h-6 border-2 border-background rounded-full shadow-lg -translate-y-1/2 -translate-x-1/2 cursor-grab active:cursor-grabbing hover:scale-110 transition-transform"
            style={{ 
              left: `${value}%`,
              backgroundColor: color
            }}
            onMouseDown={handleStart}
          />
        </div>
      </div>
      <div className="text-xs text-muted-foreground font-medium">v{versionB}</div>
    </div>
  );
};

// Image Comparison Slider Component with Proper Reveal Effect
const ImageComparisonSlider = ({ 
  heatmap, 
  documentColor, 
  versionANumber, 
  versionBNumber,
  versionAUrl,
  versionBUrl 
}: { 
  heatmap: HeatmapData; 
  documentColor: string;
  versionANumber: number;
  versionBNumber: number;
  versionAUrl: string;
  versionBUrl: string;
}) => {
  const [sliderPosition, setSliderPosition] = useState(50); // 0-100, default to middle
  const [isDragging, setIsDragging] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({
    heatmap: false,
    versionA: false,
    versionB: false
  });

  const handleSliderChange = (value: number) => {
    setSliderPosition(value);
    setIsDragging(true);
  };

  const handleSliderRelease = () => {
    // Snap back to middle (showing heatmap)
    setSliderPosition(50);
    setIsDragging(false);
  };

  const handleImageLoad = (imageType: keyof typeof imagesLoaded) => {
    setImagesLoaded(prev => ({
      ...prev,
      [imageType]: true
    }));
  };

  const allImagesLoaded = imagesLoaded.heatmap && imagesLoaded.versionA && imagesLoaded.versionB;
  const showHeatmap = !isDragging;

  return (
    <div className="mt-3 space-y-3">
      {/* Image Comparison Container */}
      <div className="relative overflow-hidden rounded-md shadow-lg">
        {!allImagesLoaded && (
          // 16:9 skeleton loader while images are loading
          <div className="w-full aspect-video bg-muted animate-pulse rounded">
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Loading page {heatmap.pageNumber}...
            </div>
          </div>
        )}
        
        {showHeatmap ? (
          // Show heatmap when in center/default position
          <img
            src={heatmap.heatmapUrl}
            alt={`Heatmap for page ${heatmap.pageNumber}`}
            className={`w-full h-auto block ${allImagesLoaded ? '' : 'hidden'}`}
            onLoad={() => handleImageLoad('heatmap')}
            onError={(e) => {
              console.error(`Failed to load heatmap for page ${heatmap.pageNumber}:`, heatmap.heatmapUrl);
              e.currentTarget.style.border = '2px solid red';
            }}
          />
        ) : (
          // Show comparison when dragging
          <div className={`relative ${allImagesLoaded ? '' : 'hidden'}`}>
            {/* Version B (Background - Right side) */}
            <img
              src={versionBUrl}
              alt={`Page ${heatmap.pageNumber} - Version B`}
              className="w-full h-auto block"
              onLoad={() => handleImageLoad('versionB')}
              onError={(e) => {
                console.error(`Failed to load Version B for page ${heatmap.pageNumber}:`, versionBUrl);
              }}
            />
            
            {/* Version A (Overlay - Left side with clipping mask) */}
            <div 
              className="absolute top-0 left-0 h-full overflow-hidden"
              style={{ width: `${sliderPosition}%` }}
            >
              <img
                src={versionAUrl}
                alt={`Page ${heatmap.pageNumber} - Version A`}
                className="block h-full object-cover"
                style={{ 
                  width: `${100 / (sliderPosition / 100)}%`,
                  maxWidth: 'none'
                }}
                onLoad={() => handleImageLoad('versionA')}
                onError={(e) => {
                  console.error(`Failed to load Version A for page ${heatmap.pageNumber}:`, versionAUrl);
                }}
              />
            </div>
            
            {/* Divider Line */}
            <div 
              className="absolute top-0 h-full w-0.5 bg-white shadow-lg z-10"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            />
            
            {/* Handle Circle on Divider Line */}
            <div 
              className="absolute top-1/2 w-4 h-4 bg-white border-2 rounded-full shadow-lg z-10 pointer-events-none"
              style={{ 
                left: `${sliderPosition}%`, 
                transform: 'translate(-50%, -50%)',
                borderColor: documentColor
              }}
            />
            
            {/* Version Labels */}
            {sliderPosition > 10 && (
              <div 
                className="absolute top-2 left-2 text-white px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: `${documentColor}CC` }}
              >
                v{versionANumber}
              </div>
            )}
            {sliderPosition < 90 && (
              <div 
                className="absolute top-2 right-2 text-white px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: `${documentColor}CC` }}
              >
                v{versionBNumber}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom Slider Control */}
      <CustomSlider
        value={sliderPosition}
        onChange={handleSliderChange}
        onRelease={handleSliderRelease}
        color={documentColor}
        versionA={versionANumber}
        versionB={versionBNumber}
      />
      
      {/* Hidden preload images for version A and B */}
      <img
        src={versionAUrl}
        alt=""
        className="hidden"
        onLoad={() => handleImageLoad('versionA')}
        onError={() => {}}
      />
      <img
        src={versionBUrl}
        alt=""
        className="hidden"
        onLoad={() => handleImageLoad('versionB')}
        onError={() => {}}
      />
    </div>
  );
};

export function HeatmapView({ document, versionA, versionB, documentColor }: HeatmapViewProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Filter state for showing only significant changes
  const [showSignificantOnly, setShowSignificantOnly] = useState(false);
  
  // Hardcoded sensitivity to 10%
  const sensitivity = 0.1;

  const { data: heatmaps, isLoading, error, refetch } = useQuery({
    queryKey: ["/api/documents", document.id, "versions", versionA?.version, "compare", versionB?.version, "heatmap"],
    queryFn: async () => {
      if (!versionA || !versionB) throw new Error('Versions not available');
      console.log(`Fetching heatmaps for versions ${versionA.version} vs ${versionB.version}`);
      const response = await fetch(`/api/documents/${document.id}/versions/${versionA.version}/compare/${versionB.version}/heatmap`);
      if (!response.ok) throw new Error('Failed to fetch heatmaps');
      const data = await response.json();
      console.log(`Fetched ${data.length} heatmaps:`, data);
      return data as HeatmapData[];
    },
    enabled: !!versionA && !!versionB, // Only fetch when both versions are available
  });

  const { data: pagesA } = useQuery({
    queryKey: ["/api/documents", document.id, "versions", versionA?.version, "pages"],
    queryFn: async () => {
      if (!versionA) throw new Error('Version A not available');
      const response = await fetch(`/api/documents/${document.id}/versions/${versionA.version}/pages`);
      if (!response.ok) throw new Error('Failed to fetch version A pages');
      const data = await response.json();
      return data.pages || [];
    },
    enabled: !!versionA,
  });

  const { data: pagesB } = useQuery({
    queryKey: ["/api/documents", document.id, "versions", versionB?.version, "pages"],
    queryFn: async () => {
      if (!versionB) throw new Error('Version B not available');
      const response = await fetch(`/api/documents/${document.id}/versions/${versionB.version}/pages`);
      if (!response.ok) throw new Error('Failed to fetch version B pages');
      const data = await response.json();
      return data.pages || [];
    },
    enabled: !!versionB,
  });

  // Pre-fetch all Version A and B images for smooth slider interaction
  useEffect(() => {
    if (!heatmaps || !pagesA || !pagesB) return;

    console.log('Pre-fetching Version A and B images for all heatmaps...');
    heatmaps.forEach((heatmap) => {
      // Pre-fetch Version A image
      const pageA = pagesA.find((p: any) => p.pageNumber === heatmap.pageNumber);
      if (pageA?.webpUrl) {
        const imageA = new Image();
        imageA.src = pageA.webpUrl;
      }

      // Pre-fetch Version B image  
      const pageB = pagesB.find((p: any) => p.pageNumber === heatmap.pageNumber);
      if (pageB?.webpUrl) {
        const imageB = new Image();
        imageB.src = pageB.webpUrl;
      }
    });
  }, [heatmaps, pagesA, pagesB]);

  // Get image URL for a specific page and version
  const getImageUrl = (pageNumber: number, version: 'heatmap' | 'A' | 'B', heatmapUrl: string): string => {
    if (version === 'heatmap') return heatmapUrl;
    
    const pages = version === 'A' ? pagesA : pagesB;
    const page = pages?.find((p: any) => p.pageNumber === pageNumber);
    return page?.webpUrl || heatmapUrl; // fallback to heatmap if no page found
  };

  // Custom Slider Component
  const CustomSlider = ({ value, onChange, onRelease, color, versionA, versionB }: { 
    value: number; 
    onChange: (value: number) => void; 
    onRelease: () => void;
    color: string;
    versionA: number;
    versionB: number;
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef<HTMLDivElement>(null);

    const updateSliderValue = (clientX: number) => {
      if (!sliderRef.current) return;
      
      const rect = sliderRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      onChange(percentage);
    };

    const handleStart = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      updateSliderValue(e.clientX);
      
      const handleMove = (e: MouseEvent) => {
        updateSliderValue(e.clientX);
      };
      
      const handleEnd = () => {
        setIsDragging(false);
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        onRelease();
      };
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
    };

    const handleTrackClick = (e: React.MouseEvent) => {
      // Only handle clicks directly on the track, not on the thumb
      if (e.target === e.currentTarget) {
        updateSliderValue(e.clientX);
        onRelease();
      }
    };

    return (
      <div className="flex items-center gap-2 px-2">
        <div className="text-xs text-muted-foreground font-medium">v{versionA}</div>
        <div className="flex-1 relative py-4">
          <div
            ref={sliderRef}
            className="relative w-full h-2 bg-muted rounded-lg cursor-pointer"
            onMouseDown={handleTrackClick}
          >
            {/* Slider Track */}
            <div className="absolute inset-0 bg-muted rounded-lg" />
            
            {/* Slider Thumb */}
            <div
              className="absolute top-1/2 w-6 h-6 border-2 border-background rounded-full shadow-lg -translate-y-1/2 -translate-x-1/2 cursor-grab active:cursor-grabbing hover:scale-110 transition-transform"
              style={{ 
                left: `${value}%`,
                backgroundColor: color
              }}
              onMouseDown={handleStart}
            />
          </div>
        </div>
        <div className="text-xs text-muted-foreground font-medium">v{versionB}</div>
      </div>
    );
  };



  console.log('HeatmapView rendered:', { 
    documentId: document.id, 
    versionA: versionA?.version, 
    versionB: versionB?.version,
    heatmapsCount: heatmaps?.length || 0,
    isLoading,
    error: error?.message
  });

  const generateHeatmapMutation = useMutation({
    mutationFn: async () => {
      if (!versionA || !versionB) throw new Error('Versions not available');
      const response = await fetch(
        `/api/documents/${document.id}/versions/${versionA.version}/compare/${versionB.version}/heatmap/generate`,
        { method: 'POST' }
      );
      if (!response.ok) throw new Error('Failed to generate heatmaps');
      return await response.json();
    },
    onSuccess: (data) => {
      console.log('Heatmap generation response:', data);
      // Trigger a refetch to show the heatmaps immediately
      refetch();
      toast({
        title: "Success",
        description: `Generated ${data.generated || 0} heatmaps successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to generate heatmaps",
        variant: "destructive",
      });
    },
  });

  const handleGenerateHeatmap = () => {
    generateHeatmapMutation.mutate();
  };

  const generatePageHeatmap = async (pageNumber: number) => {
    try {
      if (!versionA || !versionB) throw new Error('Versions not available');
      const response = await fetch(
        `/api/documents/${document.id}/versions/${versionA.version}/compare/${versionB.version}/heatmap/page/${pageNumber}`
      );
      if (!response.ok) throw new Error('Failed to generate heatmap');
      return await response.json();
    } catch (error) {
      console.error('Failed to generate heatmap:', error);
      throw error;
    }
  };

  // Filter heatmaps based on significant changes
  const filteredHeatmaps = useMemo(() => {
    if (!heatmaps) return [];
    if (!showSignificantOnly) return heatmaps;
    return heatmaps.filter(heatmap => heatmap.changePercentage > 0.1);
  }, [heatmaps, showSignificantOnly]);

  return (
    <div className="space-y-4">
      <HeatmapControls 
        documentColor={documentColor}
        onGenerateHeatmap={handleGenerateHeatmap}
        isGenerating={generateHeatmapMutation.isPending}
        hasHeatmaps={!!heatmaps && heatmaps.length > 0}
        showSignificantOnly={showSignificantOnly}
        onToggleFilter={setShowSignificantOnly}
      />
      
      {/* Vertical Scrolling Heatmaps - Browse Style Layout */}
      <div className="w-full max-w-4xl mx-auto space-y-8">
        <AnimatePresence mode="popLayout">
          {filteredHeatmaps.map((heatmap, index) => (
            <motion.div 
              key={heatmap.id} 
              className="relative"
              initial={{ 
                opacity: 0, 
                scale: 0.95,
                y: 30,
                filter: "blur(8px)"
              }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: 0,
                filter: "blur(0px)"
              }}
              exit={{ 
                opacity: 0, 
                scale: 0.95,
                y: -30,
                filter: "blur(8px)"
              }}
              transition={{ 
                duration: 0.5,
                delay: index * 0.05, // Staggered entry
                ease: [0.23, 1, 0.32, 1],
                layout: { 
                  duration: 0.3,
                  ease: [0.23, 1, 0.32, 1]
                }
              }}
              layout
            >
              {/* Page number and stats */}
              <motion.div 
                className="flex items-center justify-between mb-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.3, 
                  delay: index * 0.05 + 0.1,
                  ease: "easeOut"
                }}
              >
                <div className="inline-flex items-center gap-2 px-2 py-1 bg-background/50 rounded-full text-xs font-medium text-muted-foreground">
                  {heatmap.pageNumber}
                </div>
                <div className="text-xs text-muted-foreground">
                  {heatmap.changePercentage.toFixed(1)}% changed
                </div>
              </motion.div>
              
              {/* Image Comparison Slider */}
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.4, 
                  delay: index * 0.05 + 0.2,
                  ease: [0.23, 1, 0.32, 1]
                }}
              >
                <ImageComparisonSlider 
                  heatmap={heatmap} 
                  documentColor={documentColor} 
                  versionANumber={versionA?.version || 1}
                  versionBNumber={versionB?.version || 2}
                  versionAUrl={getImageUrl(heatmap.pageNumber, 'A', heatmap.heatmapUrl)}
                  versionBUrl={getImageUrl(heatmap.pageNumber, 'B', heatmap.heatmapUrl)}
                />
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Show message when no pages have significant changes */}
        {showSignificantOnly && filteredHeatmaps.length === 0 && heatmaps && heatmaps.length > 0 && (
          <motion.div 
            className="text-center py-12 text-muted-foreground"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ 
              duration: 0.5,
              ease: [0.23, 1, 0.32, 1]
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="space-y-2"
            >
              <div className="text-lg font-medium">No significant changes found</div>
              <div className="text-sm">All pages have changes less than 0.1%</div>
            </motion.div>
          </motion.div>
        )}
      </div>



      {(!heatmaps || heatmaps.length === 0) && !generateHeatmapMutation.isPending && !isLoading && !error && (
        <div className="text-center p-8 border border-dashed border-border rounded-lg">
          <div className="text-muted-foreground">
            No heatmaps generated yet. Click generate to create visual comparisons.
          </div>
        </div>
      )}

      {(generateHeatmapMutation.isPending || isLoading) && (
        <div className="text-center p-8 border border-dashed border-border rounded-lg">
          <div className="text-muted-foreground">
            {generateHeatmapMutation.isPending ? 'Generating heatmaps...' : 'Loading heatmaps...'}
          </div>
        </div>
      )}
    </div>
  );
}